<template>
  <div class="venue-form-page">
    <vx-card class="mb-base venue-form-header-card">
      <div class="venue-form-header">
        <div>
          <h3 class="venue-form-title">{{ pageTitle }}</h3>
          <p class="venue-form-subtitle">
            Search a Google location to autofill address fields, then complete the remaining details.
          </p>
        </div>
        <div class="venue-form-header-actions">
          <vs-button
            type="border"
            color="dark"
            class="mr-2"
            @click="$router.go(-1)"
          >Cancel</vs-button>
          <vs-button
            :disabled="!validateForm || saving"
            @click="addVenue"
          >{{ isEdit ? 'Update Venue' : 'Create Venue' }}</vs-button>
        </div>
      </div>
    </vx-card>

    <div class="vx-row">
      <div class="vx-col lg:w-7/12 w-full mb-base">
        <vx-card class="mb-base">
          <div class="form-section-header">
            <feather-icon icon="InfoIcon" svgClasses="w-4 h-4 text-primary mr-2" />
            <h5>Basic Information</h5>
          </div>

          <div class="vx-row">
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="name"
                label="Venue Name*"
                v-model="venue.name"
              />
              <span class="text-danger text-sm">{{ errors.first('name') }}</span>
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="displayname"
                label="Display Name*"
                v-model="venue.displayName"
              />
              <span class="text-danger text-sm" v-show="errors.has('displayname')">
                {{ errors.first('displayname') }}
              </span>
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-select
                name="venue-type"
                v-model="venue.type"
                label="Type of Venue"
                class="select-large w-full"
              >
                <vs-select-item
                  :key="index"
                  :value="item.id"
                  :text="item.name"
                  v-for="(item, index) in venueTypes"
                />
              </vs-select>
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-select
                name="venue-language"
                v-model="venue.lang"
                label="Language*"
                class="select-large w-full"
              >
                <vs-select-item
                  v-validate="'required'"
                  data-vv-validate-on="blur"
                  :key="index"
                  :value="item.id"
                  :text="item.name"
                  v-for="(item, index) in venueLangs"
                />
              </vs-select>
              <span class="text-danger text-sm">{{ errors.first('venue-language') }}</span>
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="venue-telephone"
                label="Telephone*"
                v-model="venue.telephone"
              />
              <span class="text-danger text-sm">{{ errors.first('venue-telephone') }}</span>
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                type="number"
                min="1"
                oninput="validity.valid||(value='');"
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="maxBooking"
                label="Max taxi bookings*"
                v-model="venue.maxBooking"
              />
              <span class="text-danger text-sm">{{ errors.first('maxBooking') }}</span>
            </div>
          </div>
        </vx-card>

        <vx-card class="mb-base">
          <div class="form-section-header">
            <feather-icon icon="MapPinIcon" svgClasses="w-4 h-4 text-primary mr-2" />
            <h5>Address Details</h5>
          </div>

          <div class="vx-row">
            <div class="vx-col w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="venue-street"
                label="Street*"
                v-model="venue.street"
              />
              <span class="text-danger text-sm">{{ errors.first('venue-street') }}</span>
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                name="venue-house-number"
                label="House number"
                v-model="venue.houseNumber"
              />
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                name="venue-suffix"
                label="Suffix"
                v-model="venue.suffix"
              />
            </div>
            <div class="vx-col md:w-1/3 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="venue-postal-code"
                label="Postal Code*"
                v-model="venue.postalCode"
              />
              <span class="text-danger text-sm">{{ errors.first('venue-postal-code') }}</span>
            </div>
            <div class="vx-col md:w-1/3 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="venue-city"
                label="City*"
                v-model="venue.city"
              />
              <span class="text-danger text-sm">{{ errors.first('venue-city') }}</span>
            </div>
            <div class="vx-col md:w-1/3 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="venue-country"
                label="Country*"
                v-model="venue.country"
              />
              <span class="text-danger text-sm">{{ errors.first('venue-country') }}</span>
            </div>
            <div class="vx-col md:w-1/3 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="venue-timezone"
                label="Timezone*"
                v-model="venue.timezone"
              />
              <span class="text-danger text-sm">{{ errors.first('venue-timezone') }}</span>
            </div>
            <div class="vx-col md:w-1/3 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="venue-latitude"
                label="Latitude*"
                v-model="venue.latitude"
              />
              <span class="text-danger text-sm">{{ errors.first('venue-latitude') }}</span>
            </div>
            <div class="vx-col md:w-1/3 w-full mb-4">
              <vs-input
                class="w-full"
                v-validate="'required'"
                data-vv-validate-on="blur"
                name="venue-longitude"
                label="Longitude*"
                v-model="venue.longitude"
              />
              <span class="text-danger text-sm">{{ errors.first('venue-longitude') }}</span>
            </div>
          </div>
        </vx-card>

        <vx-card class="mb-base">
          <div class="form-section-header">
            <feather-icon icon="CreditCardIcon" svgClasses="w-4 h-4 text-primary mr-2" />
            <h5>Payment Integration</h5>
          </div>

          <div class="vx-row">
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                name="venue-revolut-location-id"
                label="Revolut Location ID"
                placeholder="Optional"
                v-model="venue.revolut_location_id"
              />
              <small class="form-section-hint">Links this venue to its Revolut location. Leave blank if not applicable.</small>
            </div>
          </div>
        </vx-card>

        <vx-card class="mb-base">
          <div class="form-section-header">
            <feather-icon icon="MessageSquareIcon" svgClasses="w-4 h-4 text-primary mr-2" />
            <h5>Comments & Notes</h5>
          </div>

          <div class="vx-row">
            <div class="vx-col w-full mb-4">
              <vs-input
                class="w-full"
                name="venue-extra-info"
                label="Extra info for driver"
                v-model="venue.infoDriver"
              />
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                name="venue-company-comment"
                label="Company comment field"
                v-model="venue.companyComment"
              />
            </div>
            <div class="vx-col md:w-1/2 w-full mb-4">
              <vs-input
                class="w-full"
                name="venue-internal-comment"
                label="Internal comment field"
                v-model="venue.internalComment"
              />
            </div>
          </div>
        </vx-card>

        <vx-card class="mb-base">
          <div class="form-section-header">
            <feather-icon icon="MapIcon" svgClasses="w-4 h-4 text-primary mr-2" />
            <h5>Nearby Destinations</h5>
          </div>
          <nearby-destinations
            v-model="nearbyDestinations"
            :company-id="venue.companyId || $store.getters['company/getCompanyID']"
          />
        </vx-card>

        <div class="venue-form-footer">
          <vs-button
            type="border"
            color="dark"
            class="mr-2"
            @click="$router.go(-1)"
          >Cancel</vs-button>
          <vs-button
            :disabled="!validateForm || saving"
            @click="addVenue"
          >{{ isEdit ? 'Update Venue' : 'Create Venue' }}</vs-button>
        </div>
      </div>

      <div class="vx-col lg:w-5/12 w-full mb-base">
        <vx-card class="map-card sticky-map-card">
          <div class="form-section-header">
            <feather-icon icon="NavigationIcon" svgClasses="w-4 h-4 text-primary mr-2" />
            <h5>Location on Map</h5>
          </div>
          <div class="mb-4">
            <label class="vs-input--label">Search Google location</label>
            <gmap-autocomplete
              class="vs-inputx vs-input--input normal w-full"
              placeholder="Search place on Google Maps"
              :options="autocompleteOptions"
              @place_changed="setPlace"
            ></gmap-autocomplete>
            <small class="map-hint">
              Select a place or click the map to autofill address fields.
            </small>
          </div>
          <div class="map-wrap">
            <gmap-map
              ref="gmap"
              :center="center"
              :zoom="15"
              style="width: 100%; height: 420px"
              @click="getInfo($event)"
            >
              <gmap-marker :position="position"></gmap-marker>
            </gmap-map>
          </div>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import { _types, _save, _show, _update } from "@/services/Venue";
import { _list } from "@/services/Language";
import NearbyDestinations from "./NearbyDestinations.vue";

export default {
  components: {
    NearbyDestinations,
  },
  data() {
    return {
      saving: false,
      venue: {
        name: "",
        type: "",
        displayName: "",
        lang: "",
        street: "",
        houseNumber: "",
        suffix: "",
        postalCode: "",
        city: "",
        country: "",
        timezone: "",
        telephone: "",
        latitude: "",
        longitude: "",
        maxBooking: "",
        infoDriver: "",
        companyComment: "",
        internalComment: "",
        placeId: "",
        companyId: "",
        revolut_location_id: "",
      },
      nearbyDestinations: {
        popularPlaces: [],
        hotelsNearby: [],
        cityCenter: [],
      },
      editedIndex: -1,
      venueBookingCount: 0,
      position: {
        lat: 0,
        lng: 0,
      },
      currentPlace: null,
      venueTypes: [],
      venueLangs: [],
      center: {
        lat: this.$store.state.company.latitude,
        lng: this.$store.state.company.longitude,
      },
      infoContent: "",
      infoWindowPos: null,
      infoWinOpen: false,
      currentMidx: null,
      autocompleteOptions: {
        fields: [
          "name",
          "formatted_address",
          "geometry",
          "place_id",
          "address_components",
          "international_phone_number",
          "utc_offset_minutes",
        ],
      },
    };
  },
  watch: {
    "$store.state.company.companyID"() {
      this.center.lat = this.$store.state.company.latitude;
      this.center.lng = this.$store.state.company.longitude;
    },
    currentPlace() {
      this.addMarker();
    },
  },
  computed: {
    isEdit() {
      return this.editedIndex !== -1;
    },
    pageTitle() {
      return this.isEdit ? "Edit Venue" : "Add Venue";
    },
    validateForm() {
      return (
        !this.errors.any() &&
        this.venue.name != "" &&
        this.venue.displayName != "" &&
        this.venue.lang != "" &&
        this.venue.street != "" &&
        this.venue.postalCode != "" &&
        this.venue.city != "" &&
        this.venue.country != "" &&
        this.venue.timezone != "" &&
        this.venue.telephone != "" &&
        this.venue.latitude != "" &&
        this.venue.longitude != "" &&
        this.venue.maxBooking != ""
      );
    },
  },
  mounted() {
    _types().then((response) => {
      this.venueTypes = response.data.data;
    });
    _list().then((response) => {
      this.venueLangs = response.data.data;
    });
    if (this.$route.params.id) {
      this.editedIndex = this.$route.params.id;
      this.getVenue();
    }
  },
  methods: {
    fetchingData(lat, lng) {
      fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyCjUoqh0yI0tIWeo3a9jzlFRQXe9N4hu9M`
      )
        .then((result) => result.json())
        .then(
          (result) => {
            this.venue.placeId = result.results[0].place_id;
            fetch(
              `https://maps.googleapis.com/maps/api/place/details/json?place_id=${result.results[0].place_id}&key=AIzaSyCjUoqh0yI0tIWeo3a9jzlFRQXe9N4hu9M`
            )
              .then((result) => result.json())
              .then((place) => {
                place = place.result;
                this.setPlace(place, true);
                this.position = { lat: lat, lng: lng };
                this.center = { lat: lat, lng: lng };
              });
          },
          (error) => {
            console.error(error);
          }
        );
    },
    getInfo(event) {
      const marker = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      event.zoom = 15;
      this.venue.latitude = marker.lat;
      this.venue.longitude = marker.lng;
      this.position = marker;
      this.center = marker;
      this.fetchingData(marker.lat, marker.lng);
    },
    getAddressComponent(place, type, useShort = false) {
      if (!place || !place.address_components) return "";
      const component = place.address_components.find(
        (item) => item.types && item.types.includes(type)
      );
      if (!component) return "";
      return useShort ? component.short_name : component.long_name;
    },
    getLatLng(location, key) {
      if (!location) return "";
      const value = location[key];
      return typeof value === "function" ? value.call(location) : value;
    },
    setPlace(place, watcher = false) {
      if (!place) return;

      this.removeFilledData();

      const city =
        this.getAddressComponent(place, "locality") ||
        this.getAddressComponent(place, "postal_town") ||
        this.getAddressComponent(place, "administrative_area_level_2") ||
        this.getAddressComponent(place, "administrative_area_level_1");
      const country = this.getAddressComponent(place, "country");
      const postalCode = this.getAddressComponent(place, "postal_code");
      const houseNumber = this.getAddressComponent(place, "street_number");
      const route = this.getAddressComponent(place, "route");
      const lat = this.getLatLng(
        place.geometry && place.geometry.location,
        "lat"
      );
      const lng = this.getLatLng(
        place.geometry && place.geometry.location,
        "lng"
      );

      this.$set(this.venue, "city", city || "");
      this.$set(this.venue, "country", country || "");
      this.$set(this.venue, "postalCode", postalCode || "");
      this.$set(this.venue, "houseNumber", houseNumber || "");
      this.$set(
        this.venue,
        "street",
        place.formatted_address || route || place.name || ""
      );
      this.$set(
        this.venue,
        "telephone",
        place.international_phone_number || this.venue.telephone || ""
      );
      this.$set(this.venue, "placeId", place.place_id || "");

      if (!this.venue.name && place.name) {
        this.$set(this.venue, "name", place.name);
      }
      if (!this.venue.displayName && place.name) {
        this.$set(this.venue, "displayName", place.name);
      }

      if (lat !== "" && lat !== null && lat !== undefined) {
        this.$set(this.venue, "latitude", lat);
      }
      if (lng !== "" && lng !== null && lng !== undefined) {
        this.$set(this.venue, "longitude", lng);
      }

      if (watcher) {
        if (place.utc_offset !== undefined && place.utc_offset !== null) {
          this.setTimezone(place.utc_offset);
        }
        if (lat !== "" && lng !== "") {
          this.position = { lat, lng };
          this.center = { lat, lng };
        }
      } else {
        this.currentPlace = place;
        if (
          place.utc_offset_minutes !== undefined &&
          place.utc_offset_minutes !== null
        ) {
          this.setTimezone(place.utc_offset_minutes);
        }
      }
    },
    addMarker() {
      if (this.currentPlace && this.currentPlace.geometry) {
        const lat = this.getLatLng(this.currentPlace.geometry.location, "lat");
        const lng = this.getLatLng(this.currentPlace.geometry.location, "lng");
        const marker = { lat, lng };
        this.$set(this.venue, "latitude", lat);
        this.$set(this.venue, "longitude", lng);
        this.position = marker;
        this.center = marker;
      }
    },
    removeFilledData() {
      this.venue.city = "";
      this.venue.country = "";
      this.venue.street = "";
      this.venue.postalCode = "";
      this.venue.houseNumber = "";
      this.venue.timezone = "";
      this.venue.telephone = "";
      this.venue.placeId = "";
      this.currentPlace = null;
    },
    normalizeDestinations(source = {}) {
      const mapPlace = (item) => {
        if (typeof item === "number" || (typeof item === "string" && item !== "" && !isNaN(item))) {
          return {
            id: Number(item),
            name: "",
            address: "",
            latitude: "",
            longitude: "",
            placeId: "",
            type: "",
          };
        }
        return {
          id: item.id || null,
          name: item.name || "",
          address: item.address || item.street || "",
          latitude: item.latitude || "",
          longitude: item.longitude || "",
          placeId: item.placeId || item.place_id || "",
          type: item.type || "",
        };
      };
      const toArray = (value) => {
        if (Array.isArray(value)) return value.map(mapPlace);
        if (value && typeof value === "object") return [mapPlace(value)];
        return [];
      };
      return {
        popularPlaces: toArray(
          source.popularPlaces || source.popular_places || []
        ),
        hotelsNearby: toArray(
          source.hotelsNearby || source.hotels_nearby || []
        ),
        cityCenter: toArray(source.cityCenter || source.city_center || []),
      };
    },
    destinationIds(list = []) {
      return (list || [])
        .map((item) => {
          if (typeof item === "number") return item;
          if (typeof item === "string" && item !== "" && !isNaN(item)) {
            return Number(item);
          }
          return item && item.id != null ? Number(item.id) : null;
        })
        .filter((id) => id !== null && !isNaN(id));
    },
    addVenue() {
      if (this.saving) return;

      const missingPlaceIds = [
        ...(this.nearbyDestinations.popularPlaces || []),
        ...(this.nearbyDestinations.hotelsNearby || []),
        ...(this.nearbyDestinations.cityCenter || []),
      ].filter((item) => !item || item.id == null || item.id === "");

      if (missingPlaceIds.length) {
        this.$vs.notify({
          title: "Nearby destinations incomplete",
          text: "Some selected places have no ID. Remove them and add/select again before saving.",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
        });
        return;
      }

      this.saving = true;

      let data = {
        ...this.venue,
        // API expects arrays of place IDs (integers), not place objects
        popularPlaces: this.destinationIds(this.nearbyDestinations.popularPlaces),
        hotelsNearby: this.destinationIds(this.nearbyDestinations.hotelsNearby),
        cityCenter: this.destinationIds(this.nearbyDestinations.cityCenter),
      };
      if (this.editedIndex == -1) {
        data.companyId = this.$store.getters["company/getCompanyID"];
        _save(data)
          .then((response) => {
            this.$vs.notify({
              title: response.data.responseTitle,
              text: response.data.responseMessage,
              iconPack: "feather",
              icon: "icon-check-circle",
              color: "success",
            });
            this.$router.push("/venues");
          })
          .catch((error) => {
            this.$vs.notify({
              title: (error.data && error.data.responseTitle) || "Error",
              text: (error.data && error.data.responseMessage) || "Unable to save venue",
              iconPack: "feather",
              icon: "icon-cross-circle",
              color: "danger",
            });
          })
          .finally(() => {
            this.saving = false;
          });
      } else {
        if (
          this.venueBookingCount > 0 &&
          this.venue.companyId != this.$store.getters["company/getCompanyID"]
        ) {
          this.saving = false;
          this.$vs.notify({
            title: "Company Change",
            text: "Cannot change company of venue",
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger",
          });
        } else {
          data.companyId = this.$store.getters["company/getCompanyID"];

          _update(this.editedIndex, data)
            .then((response) => {
              this.$vs.notify({
                title: response.data.responseTitle,
                text: response.data.responseMessage,
                iconPack: "feather",
                icon: "icon-check-circle",
                color: "success",
              });
              this.$router.go(-1);
            })
            .catch((error) => {
              if (error.response && error.response.status == 422) {
                this.errors.add(error.response.data["errors"]);
              } else {
                this.$vs.notify({
                  title:
                    (error.response &&
                      error.response.data &&
                      error.response.data.responseTitle) ||
                    "Error",
                  text:
                    (error.response &&
                      error.response.data &&
                      error.response.data.responseMessage) ||
                    "Unable to update venue",
                  iconPack: "feather",
                  icon: "icon-cross-circle",
                  color: "danger",
                });
              }
            })
            .finally(() => {
              this.saving = false;
            });
        }
      }
    },
    setTimezone(val) {
      var hours = Math.floor(val / 60);
      if (hours < 0) {
        hours = Math.abs(hours);
        hours = hours < 10 ? "-0" + hours : "-" + hours;
      } else {
        hours = hours < 10 ? "0" + hours : hours;
      }
      var minutes = val % 60;
      var timezoneStr = "";
      timezoneStr = minutes ? hours + ":" + minutes : hours + ":00";

      this.venue.timezone = val < 0 ? timezoneStr : "+" + timezoneStr;
    },
    getVenue() {
      _show(this.editedIndex).then((response) => {
        this.venue = response.data.obj.venue;
        this.$set(
          this.venue,
          "revolut_location_id",
          this.venue.revolut_location_id || this.venue.revolutLocationId || ""
        );
        const marker = {
          lat: parseFloat(this.venue.latitude),
          lng: parseFloat(this.venue.longitude),
        };
        this.position = marker;
        this.center = marker;
        this.venueBookingCount = response.data.obj.booking_count;
        const venueData = response.data.obj.venue || {};
        const objData = response.data.obj || {};
        this.nearbyDestinations = this.normalizeDestinations({
          popularPlaces:
            venueData.popularPlaces ||
            venueData.popular_places ||
            objData.popularPlaces ||
            objData.popular_places,
          hotelsNearby:
            venueData.hotelsNearby ||
            venueData.hotels_nearby ||
            objData.hotelsNearby ||
            objData.hotels_nearby,
          cityCenter:
            venueData.cityCenter ||
            venueData.city_center ||
            objData.cityCenter ||
            objData.city_center,
        });
      });
    },
  },
};
</script>

<style scoped>
.venue-form-page {
  padding-bottom: 1.5rem;
}

.venue-form-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.venue-form-title {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #22292f;
}

.venue-form-subtitle {
  margin: 0;
  font-size: 0.8125rem;
  color: #6e6b7b;
  max-width: 34rem;
}

.venue-form-header-actions {
  display: flex;
  flex-shrink: 0;
}

.form-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.form-section-header h5 {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #22292f;
}

.form-section-hint {
  margin: 0;
  font-size: 0.8125rem;
  color: #6e6b7b;
}

.map-hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: #6e6b7b;
}

.map-wrap {
  border: 1px solid #e4e4e4;
  border-radius: 0.375rem;
  overflow: hidden;
}

.sticky-map-card {
  position: sticky;
  top: 5.5rem;
}

.venue-form-footer {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 0 1rem;
}

@media (max-width: 991px) {
  .venue-form-header {
    flex-direction: column;
  }

  .sticky-map-card {
    position: static;
  }
}
</style>
